import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  FormType,
  getCurrencyFromCountry,
  offersPickup,
  supportsFulfillment,
  TicketingFormCategory,
} from "@simplyk/common";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { v4 } from "uuid";

import { useFrontendAuctionContext } from "../../../../common/contexts/FrontendAuctionContext";
import { getDisplayGenerateReceipt, getEligibleAmountTotal } from "../../../../common/helpers/rates";
import { buildCommunicationPreferenceDefaults } from "../helpers/communicationPreferenceDefaults";
import { getBannerAspectRatio } from "../helpers/getBannerAspectRatio";
import { getPrefilledRateSelection, initTicketsPurchased } from "../helpers/getPrefilledRateSelection";
import { getVisibleAddOnRates } from "../helpers/getVisibleAddOnRates";
import { getValidRatesSorted } from "../helpers/getValidRatesSorted";
import { prefillMembershipBuyerAnswers } from "../helpers/membershipRebuyPrefill";
import { useLocaleField } from "../helpers/useLocaleField";
import { applyContactPrefill } from "../helpers/applyContactPrefill";
import { usePrefillSearchParams } from "../helpers/usePrefillSearchParams";

import { FormV2Context } from "./FormV2Context";

import { useFormMembershipInfo } from "@/canvas/features/FormV2/hooks/useFormMembershipInfo";
import { useInitFormV2FeatureFlags } from "@/canvas/features/FormV2/hooks/useInitFormV2FeatureFlags";
import { useMembershipMemberInfo } from "@/canvas/features/FormV2/hooks/useMembershipMemberInfo";
import { useSyncDisplayedTotalAmount } from "@/canvas/features/FormV2/hooks/useSyncDisplayedTotalAmount";
import { useSyncMembershipRenewal } from "@/canvas/features/FormV2/hooks/useSyncMembershipRenewal";
import {
  PreviewTicketingData,
  useTicketingFormState,
  useTicketingWithPreviewPlaceholderRates,
} from "@/canvas/features/FormV2/hooks/useTicketingFormState";
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useCommandFulfillmentVisibility } from "@/common/hooks/useCommandFulfillmentVisibility";
import { useTicketingFormUpdated } from "@/common/hooks/useTicketingFormUpdated";
import { CustomAnswer } from "@/common/types/customQuestion";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput, TicketingFrontendPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

export interface TicketingV2ProviderProps {
  type: FormType.Ticketing;
  formObject: TicketingFrontendPageOutput;
  currentStep: FormStepKey;
  setCurrentStep: (step: FormStepKey) => void;
  children: React.ReactNode;
  isEmbed: boolean;
  previewTicketingData?: PreviewTicketingData;
}

export const TicketingV2Provider: React.FC<TicketingV2ProviderProps> = ({
  formObject,
  currentStep,
  setCurrentStep,
  children,
  isEmbed,
  previewTicketingData,
}) => {
  const { isPreview } = usePreviewContext();
  const ticketing = useTicketingWithPreviewPlaceholderRates(formObject, isPreview);
  const ticketingFormState = useTicketingFormState({ ticketing, previewTicketingData, isPreview });
  const {
    questions,
    areQuestionsReady,
    areOccurrencesReady,
    occurrencesWithRates,
    selectedOccurrenceId,
    selectedOccurrenceWithRates,
    validDiscount,
    setMembershipRenewalBuyerInfo,
  } = ticketingFormState;
  const { commandBidsRateIdsMap, highestBidsMapByRateId, hasAlreadyBid } = useFrontendAuctionContext();
  const { setSelectedOccurrence } = useContext(FrontendFormContext);
  const [isReady, setIsReady] = useState(false);
  const getLocaleField = useLocaleField();
  const {
    contactInfo: prefillSearchParams,
    rateId: prefillRateId,
    isMemberSomeoneElse,
    buyerDetails,
    isPrefillPending,
  } = usePrefillSearchParams(ticketing.formCategory);

  const rates = useMemo(() => ticketing.rates || [], [ticketing.rates]);
  const isAuction = ticketing.formCategory === TicketingFormCategory.Auction;
  const { locale } = useLocaleContext();

  const hasMemberOnlyRates = rates.some((rate) => rate.isMemberOnly);
  const isMembershipForm = ticketing.formCategory === TicketingFormCategory.MembershipV2;
  const membershipInfo = useFormMembershipInfo({
    organizationId: ticketing.organizationId ?? undefined,
    locale,
    hasMemberOnlyRates,
    isMembershipForm,
    ticketingId: ticketing.id,
  });
  const memberInfo = useMembershipMemberInfo({
    formCategory: ticketing.formCategory,
    rates,
  });

  const commandFulfillment = useCommandFulfillmentVisibility(ticketing, hasAlreadyBid);

  const featureFlags = useInitFormV2FeatureFlags();

  const [validationMode, setValidationMode] = useState<"onBlur" | "onChange">("onBlur");
  const bannerAspectRatio = useMemo(
    () => getBannerAspectRatio(ticketing.isSquareBanner, ticketing.bannerVideoUrl),
    [ticketing.isSquareBanner, ticketing.bannerVideoUrl]
  );

  const auctionCustomAnswersOfParticipants = useMemo(() => {
    return rates.map((rate) => ({ rateId: rate.id, answers: [] }));
  }, [rates]);

  // The experiment is over but will rollout the changes later, so we keep the code but always set "a" for now

  const communicationPreferenceDefaults = useMemo(
    () => buildCommunicationPreferenceDefaults(formObject.communicationPreferences, formObject.organization?.country),
    [formObject.communicationPreferences, formObject.organization?.country]
  );

  const form = useForm<TicketingPaymentInput>({
    defaultValues: {
      sepaMandateAccepted: false,
      ticketsPurchased: initTicketsPurchased(rates, isPreview),
      customAnswersOfParticipants: isAuction ? auctionCustomAnswersOfParticipants : [],
      customAnswersOfOrder: [],
      shippingSameAsBilling: true,
      ...communicationPreferenceDefaults,
    },
    mode: validationMode,
    shouldFocusError: true /* Custom error scroll on step 1, but leave this true for step 2 */,
  });
  const { control, reset, setValue } = form;

  useEffect(() => {
    if (selectedOccurrenceId) {
      form.setValue("occurrenceId", selectedOccurrenceId);
    }
  }, [form, selectedOccurrenceId]);

  // The payment step asks the server for its display, and bank availability depends on the selected event date.
  useEffect(() => {
    setSelectedOccurrence(selectedOccurrenceWithRates?.occurrence ?? null);
  }, [setSelectedOccurrence, selectedOccurrenceWithRates?.occurrence]);

  useEffect(() => {
    if (commandFulfillment.defaultFulfillmentMethod) {
      form.setValue("fulfillmentMethod", commandFulfillment.defaultFulfillmentMethod);
    }
  }, [form, commandFulfillment.defaultFulfillmentMethod]);

  if (!isPreview) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTicketingFormUpdated({ control, ticketing, validDiscount });
  }

  useSyncDisplayedTotalAmount({ control, ticketing, validDiscount, isAuction });
  useSyncMembershipRenewal({ control, setValue, ticketing, setMembershipRenewalBuyerInfo });

  const extraDonation = useWatch({
    control,
    name: "extraDonation",
  });
  const ticketsPurchased = useWatch({
    control,
    name: "ticketsPurchased",
  });

  const needsGroupAvailability =
    !isPreview && Boolean(rates.find((rate) => rate.id === prefillRateId)?.groupMembershipMaxMembers);

  const resetForm = useCallback(() => {
    const selection = getPrefilledRateSelection({
      rates,
      questions,
      prefillRateId,
      isPreview,
      isMemberSomeoneElse,
      availability: needsGroupAvailability
        ? { seats: ticketing.seats, rates: selectedOccurrenceWithRates?.rates ?? [] }
        : undefined,
    });
    reset({
      extraDonation,
      ticketsPurchased: selection.ticketsPurchased,
      customAnswersOfParticipants: selection.customAnswersOfParticipants,
      customAnswersOfOrder: prefillMembershipBuyerAnswers(
        initCustomAnswersOfOrder(questions),
        buyerDetails?.customAnswersOfOrder
      ),
      giveAsOrganism: buyerDetails?.giveAsOrganism,
      companyName: buyerDetails?.companyName,
      occurrenceId: selectedOccurrenceId || undefined,
      // reset() replaces every value, so the preselected default has to be restated here too.
      ...communicationPreferenceDefaults,
      ...prefillSearchParams,
      sepaMandateAccepted: false,
    });
  }, [
    reset,
    extraDonation,
    rates,
    isPreview,
    questions,
    selectedOccurrenceId,
    prefillSearchParams,
    prefillRateId,
    isMemberSomeoneElse,
    buyerDetails,
    communicationPreferenceDefaults,
    needsGroupAvailability,
    selectedOccurrenceWithRates,
    ticketing.seats,
  ]);

  useEffect(() => {
    if (isReady || !areQuestionsReady) {
      return;
    }
    if (prefillRateId && rates.length === 0) {
      return;
    }
    if (
      needsGroupAvailability &&
      (!areOccurrencesReady || (occurrencesWithRates.length > 0 && !selectedOccurrenceWithRates))
    ) {
      return;
    }
    resetForm();
    setIsReady(true);
  }, [
    areQuestionsReady,
    areOccurrencesReady,
    needsGroupAvailability,
    occurrencesWithRates.length,
    selectedOccurrenceWithRates,
    rates,
    isReady,
    resetForm,
    prefillRateId,
  ]);

  useEffect(() => {
    if (!isReady || isPrefillPending) {
      return;
    }
    applyContactPrefill(form, prefillSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Apply the email prefill once it resolves after the form was initialized
  }, [isReady, isPrefillPending]);

  const hasLocation = supportsFulfillment(ticketing.formCategory)
    ? Boolean(ticketing.address) && offersPickup(commandFulfillment.offeredMethods)
    : Boolean(ticketing.address);

  const hasSoldOutItem = Boolean(
    selectedOccurrenceWithRates?.rates?.some((r) => (r.remainingTickets ?? Infinity) <= 0)
  );
  const soldOutItemToBottom = hasSoldOutItem && rates.length > 2;
  const memberOnlyRatesToTop = !isPreview && membershipInfo.isAuthenticated && hasMemberOnlyRates;

  const validRatesSorted = useMemo(() => {
    return getValidRatesSorted({
      rates,
      isAuction,
      commandBidsRateIdsMap,
      highestBidsMapByRateId,
      selectedOccurrenceWithRates: selectedOccurrenceWithRates ?? null,
      soldOutItemToBottom,
      memberOnlyRatesToTop,
    });
  }, [
    rates,
    isAuction,
    commandBidsRateIdsMap,
    highestBidsMapByRateId,
    selectedOccurrenceWithRates,
    soldOutItemToBottom,
    memberOnlyRatesToTop,
  ]);

  const addOnRates = useMemo(() => getVisibleAddOnRates(rates), [rates]);
  const hasAddOnsStep = addOnRates.length > 0;

  const hasDate = Boolean(occurrencesWithRates.length >= 1 && occurrencesWithRates[0].occurrence);
  const hasBanner = Boolean(ticketing.bannerUrl || ticketing.bannerVideoUrl);
  const isMembership = ticketing.formCategory === TicketingFormCategory.MembershipV2;
  const formFields = ticketing.ticketingFields || [];
  const localeField = getLocaleField(formFields)!;
  const description = localeField?.sanitizedDescription ?? undefined;

  const formOrganization = ticketing.organization!;

  const eligibleAmountTotal = getEligibleAmountTotal(
    ticketsPurchased,
    ticketing,
    extraDonation ?? 0,
    validDiscount,
    validDiscount?.selectedRateIds ?? []
  );
  const displayGenerateReceipt =
    getDisplayGenerateReceipt(ticketsPurchased, ticketing, extraDonation ?? 0) && formOrganization.canGenerateReceipt;

  // If true, this event form is linked a a peer to peer campaign
  const isP2PLinkedEvent = ticketing.formCategory === TicketingFormCategory.Event && Boolean(ticketing.donationForm);

  return (
    <FormProvider {...form}>
      <FormV2Context.Provider
        value={{
          ...ticketingFormState,
          type: FormType.Ticketing,
          featureFlags,
          formObject: ticketing,
          form,
          currentStep,
          setCurrentStep,
          validRatesSorted,
          addOnRates,
          hasAddOnsStep,
          resetForm,
          hasDate,
          hasLocation,
          numberOfOccurrences: occurrencesWithRates.length,
          setValidationMode,
          hasBanner,
          isMembership,
          isAuction,
          formFields,
          localeField,
          isP2PLinkedEvent,
          description,
          formCategory: ticketing.formCategory,
          questions,
          communicationPreferences: formObject.communicationPreferences || [],
          communicationPreferencesTitle: localeField?.communicationPreferencesTitle ?? null,
          ticketsPurchased,
          formOrganization,
          formCountry: ticketing.organization?.country,
          formCurrency: getCurrencyFromCountry(formOrganization?.country),
          displayGenerateReceipt,
          eligibleAmountTotal,
          itemType: isMembership
            ? "membership"
            : ticketing.formCategory === TicketingFormCategory.Event
              ? "ticket"
              : "item",
          bannerAspectRatio,
          publishedOrgPageUrl: ticketing.publishedOrgPageUrl ?? null,
          isEmbed,
          isPreview,
          membershipInfo,
          memberInfo,
          commandFulfillment,
        }}
      >
        {children}
      </FormV2Context.Provider>
    </FormProvider>
  );
};

const initCustomAnswersOfOrder = (questions: QuestionOutput[]) => {
  return questions.filter((question) => question.hasRate === false).map(initAnswer);
};

export const initAnswer = (question: QuestionOutput): CustomAnswer => {
  return {
    questionId: question.id,
    type: question.type,
    id: v4(),
    answers: [],
    choiceIds: [],
  };
};
